import { type ReactElement, useCallback, useEffect, useState } from 'react';
import {
  type DeliveryClaimOutcome,
  type DeliveryClaimView,
  type DeliveryEvidenceEntryView,
  type DeliveryOpsAuditEntryView,
} from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, Drawer, EmptyState, Field, ListTable, MoneyInput, Notice, PageHeader, Select, type Column, DrawerHeader, DrawerBody, DrawerFooter, SectionTitle, Stack, Text, HairlineRow, Spacer } from '@vello/web-theme';
import {
  CLAIM_DECISION_REASONS,
  CLAIM_OUTCOMES,
  claimActions,
  claimActorsAreLegacy,
  claimTone,
  formatMarketInstant,
  mayDecideClaims,
  mayManageClaims,
  scopeTimeZone,
  tailOf,
} from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { LoadingBlock } from '../components/loading-block';
import { DrawerStack } from '../components/drawer-stack';
import { IdRef } from '../components/code-ref';

/**
 * Send › Reclamações (W6-ADMIN-SCREENS, D401) — the Admin's side of `/v1/delivery-ops/claims`, a platform surface
 * (`claim:view` / `claim:manage` / `claim:decide`) that only the fleet console ever called.
 *
 * **It builds on W6-SESSION-ACTORS (D397).** Before that front, `decide` and the evidence routes took the actor from
 * the request body, so any operator could record a decision under someone else's name. They now take the signed-in
 * operator from the session and refuse an identity field with `IDENTITY_FROM_SESSION_ONLY` — so this screen sends
 * **no actor at all**, and a claim written before D397 is labelled `LEGACY_BODY` rather than shown as if its decider
 * had been authenticated.
 *
 * The compensation is entered in currency units through the design-system money input (never minor units) and is
 * capped by the server at the parcel's declared value; the decision always carries a stable reason code.
 */
export function SendClaimsScreen(): ReactElement {
  const { runtime, scope, markets, grants } = useAdmin();
  const { toast } = useConsole();
  const { t, lang } = useT();
  const { v, fail } = useVocab();
  const timeZone = scopeTimeZone(scope.current, markets);
  const mayDecide = mayDecideClaims(grants);
  // Review W6N-09: `investigate` and `close` are `claim:manage` on the server; only `decide` is `claim:decide`.
  const mayManage = mayManageClaims(grants);

  const [claims, setClaims] = useState<readonly DeliveryClaimView[] | undefined>(undefined);
  const [listError, setListError] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState<DeliveryClaimView | undefined>(undefined);
  const [evidence, setEvidence] = useState<readonly DeliveryEvidenceEntryView[] | undefined>(undefined);
  const [audit, setAudit] = useState<readonly DeliveryOpsAuditEntryView[] | undefined>(undefined);
  const [outcome, setOutcome] = useState<DeliveryClaimOutcome>('upheld');
  const [reasonCode, setReasonCode] = useState<string>(CLAIM_DECISION_REASONS[0] as string);
  const [amountMinor, setAmountMinor] = useState<bigint | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | undefined>(undefined);

  const money = useCallback(
    (m: { readonly amountMinor: string; readonly currency: string }): string =>
      /^-?\d+$/.test(m.amountMinor) && isIso4217Currency(m.currency)
        ? formatMoney({ amountMinor: BigInt(m.amountMinor), currency: m.currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' })
        : `${m.amountMinor} ${m.currency}`,
    [lang],
  );

  const load = useCallback(async (): Promise<void> => {
    const res = await runtime.deliveryOps.listOpen();
    if (res.ok) {
      setClaims(res.value.claims);
      setListError(undefined);
    } else {
      setClaims((prev) => prev ?? []);
      setListError(res.error.code === 'FORBIDDEN' ? t('w6.refused.forbidden') : t('w6.refused', { code: fail(res.error) }));
    }
  }, [runtime, t]);

  useEffect(() => {
    void load();
  }, [load]);
  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.queue });

  const openDetail = async (claim: DeliveryClaimView): Promise<void> => {
    setOpen(claim);
    setEvidence(undefined);
    setAudit(undefined);
    setActionError(undefined);
    setAmountMinor(undefined);
    const res = await runtime.deliveryOps.get(claim.claimId);
    if (res.ok) {
      setOpen(res.value.claim);
      setEvidence(res.value.evidence);
      setAudit(res.value.audit);
    } else {
      setEvidence([]);
      setAudit([]);
    }
  };

  /** Run one claim transition and fold the answer back into the drawer + the list. */
  const run = async (claim: DeliveryClaimView, action: 'investigate' | 'decide' | 'close'): Promise<void> => {
    setBusy(true);
    setActionError(undefined);
    const res =
      action === 'investigate'
        ? await runtime.deliveryOps.investigate(claim.claimId)
        : action === 'close'
          ? await runtime.deliveryOps.close(claim.claimId)
          : await runtime.deliveryOps.decide(claim.claimId, {
              outcome,
              // The server caps the compensation at the declared value; a rejected outcome books nothing.
              requestedCompensation: { amountMinor: String(outcome === 'rejected' ? 0n : (amountMinor ?? 0n)), currency: claim.declaredValue.currency },
              reasonCode,
            });
    setBusy(false);
    if (res.ok) {
      setOpen(res.value.claim);
      toast({ message: t(action === 'decide' ? 'sclaims.decide.done' : 'sclaims.state.changed'), tone: 'success' });
      await load();
      return;
    }
    setActionError(t('w6.actionRefused', { code: fail(res.error) }));
  };

  const columns: readonly Column<DeliveryClaimView>[] = [
    { key: 'claim', header: t('sclaims.col.claim'), width: 'minmax(110px, 0.9fr)', render: (c) => <IdRef value={c.claimId} /> },
    { key: 'delivery', header: t('sclaims.col.delivery'), width: 'minmax(110px, 0.9fr)', render: (c) => <IdRef value={c.deliveryId} /> },
    { key: 'reason', header: t('sclaims.col.reason'), width: 'minmax(120px, 1fr)', render: (c) => v('delivery.claim_reason', c.reason) },
    { key: 'state', header: t('sclaims.col.state'), width: 'minmax(130px, 1fr)', render: (c) => <Badge tone={claimTone(c.state)}>{v('delivery.claim_state', c.state)}</Badge> },
    { key: 'value', header: t('sclaims.col.value'), width: 'minmax(110px, 0.9fr)', render: (c) => <span className="tnum">{money(c.declaredValue)}</span> },
    { key: 'opened', header: t('sclaims.col.opened'), width: 'minmax(140px, 1fr)', render: (c) => <Text role="caption" tone="secondary" mono>{formatMarketInstant(c.createdAt, timeZone, lang)}</Text> },
  ];

  const actions = open === undefined ? [] : claimActions(open);

  return (
    <>
      <PageHeader
        title={t('sclaims.title')}
        subtitle={t('sclaims.subtitle')}
      />


      <ListTable
        columns={columns}
        rows={claims ?? []}
        rowKey={(c) => c.claimId}
        onRowClick={(c) => void openDetail(c)}
        selectedKey={open?.claimId}
        loading={claims === undefined}
        loadingLabel={t('ui.loading')}
        error={listError}
        onRetry={() => void load()}
        retryLabel={t('ui.retry')}
        empty={<EmptyState title={t('sclaims.empty.title')} detail={t('sclaims.empty.detail')} />}
        minWidth={980}
      />

      <Drawer open={open !== undefined} onClose={() => setOpen(undefined)} labelledBy="sclaims-detail-title">
        {open !== undefined && (
          <>
            <DrawerHeader titleId="sclaims-detail-title" title={t('sclaims.detail.title', { id: tailOf(open.claimId) })} subtitle={<Badge tone={claimTone(open.state)}>{v('delivery.claim_state', open.state)}</Badge>} />
            <DrawerBody>
              <DrawerStack>
                {claimActorsAreLegacy(open) && <Notice tone="warning">{t('w6.legacyActors')}</Notice>}
                {(((actions.includes('investigate') || actions.includes('close')) && !mayManage) || (actions.includes('decide') && !mayDecide)) && (
                  <Notice tone="neutral">{t('w6.noPermission.action')}</Notice>
                )}
                {actionError !== undefined && <Notice tone="danger">{actionError}</Notice>}

                {open.decision !== undefined && (
                  <Notice tone={open.decision.outcome === 'rejected' ? 'danger' : 'success'}>
                    {t('sclaims.decision.summary', {
                      outcome: v('delivery.claim_outcome', open.decision.outcome),
                      amount: money(open.decision.compensation),
                      reason: v('delivery.claim_decision_reason', open.decision.reasonCode),
                    })}
                  </Notice>
                )}

                {actions.includes('decide') && mayDecide && (
                  <Stack gap={3} as="section">
                    <Text as="h3" role="label">{t('sclaims.decide.title', { id: tailOf(open.claimId) })}</Text>
                    <Field label={t('sclaims.decide.outcome')} htmlFor="sclaims-outcome">
                      <Select id="sclaims-outcome" value={outcome} onChange={(e) => setOutcome(e.target.value as DeliveryClaimOutcome)}>
                        {CLAIM_OUTCOMES.map((o) => (
                          <option key={o} value={o}>{v('delivery.claim_outcome', o)}</option>
                        ))}
                      </Select>
                    </Field>
                    {outcome !== 'rejected' && (
                      <MoneyInput
                        id="sclaims-amount"
                        label={t('sclaims.decide.compensation')}
                        currency={open.declaredValue.currency}
                        locale={lang}
                        valueMinor={amountMinor}
                        onChangeMinor={setAmountMinor}
                        issueText={(issue) => t(`money.issue.${issue}`)}
                        hint={t('sclaims.decide.cap', { value: money(open.declaredValue) })}
                        {...(/^\d+$/.test(open.declaredValue.amountMinor) ? { maxMinor: BigInt(open.declaredValue.amountMinor) } : {})}
                        required
                      />
                    )}
                    <Field label={t('w6.reason.label')} htmlFor="sclaims-reason">
                      <Select id="sclaims-reason" value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}>
                        {CLAIM_DECISION_REASONS.map((code) => (
                          <option key={code} value={code}>{v('delivery.claim_decision_reason', code)}</option>
                        ))}
                      </Select>
                    </Field>
                  </Stack>
                )}

                <section>
                  <SectionTitle level={3} size="sub">{t('sclaims.evidence.title')}</SectionTitle>
                  {evidence === undefined ? (
                    <LoadingBlock lines={2} />
                  ) : evidence.length === 0 ? (
                    <Text as="p" role="caption" tone="secondary">{t('sclaims.evidence.empty')}</Text>
                  ) : (
                    evidence.map((e) => (
                      <HairlineRow key={e.evidenceId}>
                        <span>{e.kind !== undefined ? v('delivery.evidence_kind', e.kind) : v('delivery.ops_audit_kind', e.type)}</span>
                        <Text role="caption" tone="secondary" mono>{formatMarketInstant(e.at, timeZone, lang)}</Text>
                      </HairlineRow>
                    ))
                  )}
                </section>

                <section>
                  <SectionTitle level={3} size="sub">{t('sclaims.audit.title')}</SectionTitle>
                  {(audit ?? []).map((a) => (
                    <HairlineRow key={a.entryId}>
                      <span>{v('delivery.ops_audit_kind', a.kind)}</span>
                      <Text role="caption" tone="secondary" mono>{formatMarketInstant(a.at, timeZone, lang)}</Text>
                    </HairlineRow>
                  ))}
                </section>
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              <Button variant="ghost" onClick={() => setOpen(undefined)}>{t('ui.close')}</Button>
              <Spacer />
              {actions.includes('investigate') && mayManage && (
                <Button variant="secondary" disabled={busy} onClick={() => void run(open, 'investigate')}>{t('sclaims.action.investigate')}</Button>
              )}
              {actions.includes('close') && mayManage && (
                <Button variant="secondary" disabled={busy} onClick={() => void run(open, 'close')}>{t('sclaims.action.close')}</Button>
              )}
              {actions.includes('decide') && mayDecide && (
                <Button variant="primary" disabled={busy} onClick={() => void run(open, 'decide')}>{t('sclaims.decide.submit')}</Button>
              )}
            </DrawerFooter>
          </>
        )}
      </Drawer>
    </>
  );
}
